import type { AgentListResponse } from '../agents/schema';
import type { ReferenceCapture } from '../references/schema';

export type IndicatorTone = 'ok' | 'warning' | 'unknown';

export interface Indicator {
  readonly tone: IndicatorTone;
  readonly message: string;
}

/** The Windows agent indicator: is Excel delivery currently reachable at all
 * (`docs/architecture.md` §3, `backend/app/api/status.py`'s `GET /agents`). Indicator only -
 * resolving a stale or revoked agent is not this screen's job. */
export function agentIndicator(agents: AgentListResponse | undefined, isError: boolean): Indicator {
  if (isError) {
    return { tone: 'unknown', message: 'No se pudo verificar el agente de Excel.' };
  }
  if (agents === undefined) {
    return { tone: 'unknown', message: 'Verificando el agente de Excel…' };
  }
  if (agents.delivery_available) {
    const activeAgents = agents.items.filter((agent) => agent.status === 'active' && !agent.is_stale);
    const workbookConfirmed = activeAgents.some(
      (agent) => agent.last_report != null && !agent.last_report.is_stale && agent.last_report.workbook_availability === 'available',
    );
    if (!workbookConfirmed) {
      return {
        tone: 'warning',
        message: 'Excel: agente conectado, pero no puede ver el libro de Excel en esa PC.',
      };
    }
    return { tone: 'ok', message: 'Excel: agente conectado.' };
  }
  return {
    tone: 'warning',
    message: 'Excel: agente sin conexión reciente. El envío a Excel puede tardar.',
  };
}

/** The reference-capture indicator: whether the customer/product lists Excel published are
 * recent enough to trust their *absence* of a name (`backend/app/reporting/references.py`:
 * "staleness is reported, never enforced"). `null` (never synced) counts as stale. */
export function referenceIndicator(
  customers: ReferenceCapture | null | undefined,
  customersIsError: boolean,
  products: ReferenceCapture | null | undefined,
  productsIsError: boolean,
): Indicator {
  if (customersIsError && productsIsError) {
    return { tone: 'unknown', message: 'No se pudo verificar las listas de Excel.' };
  }
  const customersLoaded = customersIsError || customers !== undefined;
  const productsLoaded = productsIsError || products !== undefined;
  if (!customersLoaded && !productsLoaded) {
    return { tone: 'unknown', message: 'Verificando las listas de Excel…' };
  }

  const stale: string[] = [];
  if (!customersIsError && (customers === null || customers?.is_stale)) {
    stale.push('clientes');
  }
  if (!productsIsError && (products === null || products?.is_stale)) {
    stale.push('productos');
  }

  if (stale.length === 0) {
    return { tone: 'ok', message: 'Listas de clientes y productos de Excel actualizadas.' };
  }
  return {
    tone: 'warning',
    message: `Listas de ${stale.join(' y ')} de Excel desactualizadas. Un valor nuevo puede no aparecer todavía.`,
  };
}
